import type { SavedRoofModulesLayout } from '../../@types/types';
import type { Viewer } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Viewer-Factory/Viewer';
import {
    captureLiveModulesLayout,
    getCenterPoint,
    getModuleMeshInfo,
    getRoofModuleMeshes,
} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/Common';
import { restoreModulesLayoutFromSnapshot } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/DefaultLayoutControl';
import {
    SetCameraView,
    SetTopFitView,
} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/CameraControl';
import {
    addModulesToAllRoofsMaxFill,
    DeleteModule,
    SetModuleGraph,
} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/ModuleControl';
import { remapModulesLayoutToViewerWithMap } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/layout';
import { roundedBasedOn33Percent } from '../../services/oneButtonServices/designServices';
import { useViewerStore } from '../../store/store';
import { preContractIrrColorToCss } from './preContractModuleLayout';
import type {
    PreContractFillOrientation,
    PreContractModuleCell,
    PreContractRoofModuleLayout,
    PreContractSiteTree,
} from './preContractTypes';
import { normalizeFillOrientations } from './preContractTypes';
import { applyTreeGhostAppearance } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/TreeGhostVisual';
import {
    beginManualGhostToggle,
    clearTreeCutScenarioGhostState,
    ghostTreeForTreeCutScenario,
} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/TreeImpactEligibility';
import { MakeCSSLabel } from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/LabelControl';
import {
    cancelShadeCalculation,
    getShadeRunGeneration,
    RefreshShade,
} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/RefreshShade';
import {
    collectFinanceEligibilityCurrentValues,
    pickFinanceEligibilitySceneValues,
    type FinanceEligibilitySceneValues,
} from '../../components/Layout-Components/Editor-Layout/Three-JS-Viewer/Controllers/FinanceEligibilityControl';
import { ACCESS_MULTIPLIERS, siteCalculationState } from '../../services/siteCalculationState';
import { normalizeLossesVendor } from '../../Constants';
import { Box3, Vector3 } from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

export const PCI_ONEDRAW_ROOFS_MSG = 'pci-onedraw-roofs';
export const PCI_ONEDRAW_LOAD_ERROR_MSG = 'pci-onedraw-load-error';
export const PCI_ONEDRAW_ROOF_SELECTION_MSG = 'pci-onedraw-roof-selection';
export const PCI_ONEDRAW_MODULE_SELECTION_MSG = 'pci-onedraw-module-selection';
export const PCI_ONEDRAW_VIEWPORT_RESIZE_MSG = 'pci-onedraw-viewport-resize';
export const PCI_ONEDRAW_CAPTURE_MODULES_MSG = 'pci-onedraw-capture-modules';
export const PCI_ONEDRAW_MODULES_LAYOUT_MSG = 'pci-onedraw-modules-layout';
export const PCI_ONEDRAW_SIMULATE_OPTIONS_MSG = 'pci-onedraw-simulate-options';
export const PCI_ONEDRAW_SIMULATE_RESULTS_MSG = 'pci-onedraw-simulate-results';
export const PCI_ONEDRAW_MAXFILL_MSG = 'pci-onedraw-maxfill';
export const PCI_ONEDRAW_MAXFILL_RESULT_MSG = 'pci-onedraw-maxfill-result';
export const PCI_ONEDRAW_APPLY_MAXFILL_MSG = 'pci-onedraw-apply-maxfill';
export const PCI_ONEDRAW_APPLY_MAXFILL_DONE_MSG = 'pci-onedraw-apply-maxfill-done';
export const PCI_ONEDRAW_TREE_GHOST_MSG = 'pci-onedraw-tree-ghost';
export const PCI_ONEDRAW_LOSSES_VENDOR_MSG = 'pci-onedraw-losses-vendor';
export const PCI_ONEDRAW_3D_VIEW_MSG = 'pci-onedraw-3d-view';
export const PCI_ONEDRAW_APPLY_DESIGN_MSG = 'pci-onedraw-apply-design';
export const PCI_ONEDRAW_APPLY_DESIGN_DONE_MSG = 'pci-onedraw-apply-design-done';
export const PCI_ONEDRAW_LOAD_VERSION_MSG = 'pci-onedraw-load-version';
export const PCI_ONEDRAW_LOAD_VERSION_DONE_MSG = 'pci-onedraw-load-version-done';
export const PCI_ONEDRAW_CANCEL_SHADE_MSG = 'pci-onedraw-cancel-shade';

type PciPendingAbort = () => void;
let pciPendingViewportAbort: PciPendingAbort | null = null;

function abortPendingPciViewportRequest() {
    const abort = pciPendingViewportAbort;
    pciPendingViewportAbort = null;
    abort?.();
}

function trackPciViewportRequest(abort: PciPendingAbort): () => void {
    abortPendingPciViewportRequest();
    pciPendingViewportAbort = abort;
    return () => {
        if (pciPendingViewportAbort === abort) pciPendingViewportAbort = null;
    };
}
export const PCI_ONEDRAW_FINALIZE_MSG = 'pci-onedraw-finalize';
export const PCI_ONEDRAW_FINALIZE_SNAPSHOT_MSG = 'pci-onedraw-finalize-snapshot';
export const PCI_ONEDRAW_FINALIZE_SNAPSHOT_RESULT_MSG = 'pci-onedraw-finalize-snapshot-result';
export const PCI_ONEDRAW_FINALIZE_SAVE_MSG = 'pci-onedraw-finalize-save';
export const PCI_ONEDRAW_FINALIZE_SAVE_DONE_MSG = 'pci-onedraw-finalize-save-done';
export const PCI_ONEDRAW_ELIGIBILITY_MSG = 'pci-onedraw-eligibility';
export const PCI_ONEDRAW_EMBED_FRAME_ID = 'pci-onedraw-embed-frame';

/** Scene-derived eligibility reads the embed publishes to the pre-contract parent. */
export type PciEligibilitySceneValues = FinanceEligibilitySceneValues;

export type PciFinalizeSnapshot = {
    ready: boolean;
    readyMessage: string | null;
    gateIssues: string[];
    setbackNotes: string;
    detachedStructureBuildingIds: number[];
    isInActiveDesign: boolean;
    designID: number;
    rsaNotes: string;
    rsaStatus?: string;
    customerSiteRestrictions: string;
    annualUsage: number | null;
    epcEstimatedProduction: number | null;
};

export type PciFinalizeSaveInput = {
    rsaStatus?: string;
    rsaNotes: string;
    customerSiteRestrictions: string;
    annualUsage: number | null;
    epcEstimatedProduction: number | null;
    acceptEfficiencyOverride?: boolean;
};

export type PciFinalizeSaveResult = {
    ok: boolean;
    title: string;
    message: string;
    needsEfficiencyOverride?: boolean;
    efficiency?: number;
    solarAccess?: number;
};

export type PciSiteRoof = {
    roofFaceId: string;
    roofTagId: number;
    label: string;
    buildingName?: string;
    /** Per-face Low squares from the viewer Roof Square Table. */
    lowSlopeSquare?: number;
    /** Per-face Normal squares from the viewer Roof Square Table (includes Steep). */
    normalSlopeSquare?: number;
};

export type PciSiteTree = {
    id: string;
    label: string;
    ghosted: boolean;
    multiplier: number;
    /** Viewer mesh height in meters, used for Tree Calculator cut cost. */
    heightMeters?: number;
    /** Viewer trunk radius in meters, used for Tree Calculator DBH. */
    trunkRadiusMeters?: number;
};

function isSmallFace(mesh: { userData?: { roof_size_type?: string } }): boolean {
    return mesh.userData?.roof_size_type === 'small_face';
}

function roofSquareLookupFromViewer(): Map<number, { low: number; normal: number }> {
    const map = new Map<number, { low: number; normal: number }>();
    const table = useViewerStore.getState().roofSquareTableData;
    if (!Array.isArray(table)) return map;
    table.forEach((building) => {
        const rows = Array.isArray(building?.data) ? building.data : [];
        rows.forEach((row) => {
            const id = Number(row?.roofId);
            if (!Number.isFinite(id)) return;
            map.set(id, {
                low: Number(row.lowSlopeSquare) || 0,
                normal: Number(row.normalSlopeSquare) || 0,
            });
        });
    });
    return map;
}

function squaresFromMeshUserData(mesh: any): { low: number; normal: number } {
    const rs = mesh?.userData?.roofSquare;
    return {
        low: Number(rs?.lowSquare) || 0,
        normal: Number(rs?.normalSquare) || 0,
    };
}

/** True when at least one published roof carries viewer square values. */
export function pciRoofsHaveSquares(roofs: PciSiteRoof[]): boolean {
    return roofs.some(
        (roof) => (Number(roof.lowSlopeSquare) || 0) + (Number(roof.normalSlopeSquare) || 0) > 0,
    );
}

/**
 * Site Low/Normal totals for currently selected PCI roofs.
 * Matches Roof Square Table → Total Site Summary rounding.
 */
export function sumPciSelectedRoofSquares(
    roofs: PciSiteRoof[],
    selections: boolean[],
): { low: number; standard: number; total: number } {
    const buildings = new Map<string, { low: number; standard: number }>();
    roofs.forEach((roof, index) => {
        if (selections[index] === false) return;
        const key = String(roof.buildingName || '_');
        const cur = buildings.get(key) ?? { low: 0, standard: 0 };
        cur.low += Number(roof.lowSlopeSquare) || 0;
        cur.standard += Number(roof.normalSlopeSquare) || 0;
        buildings.set(key, cur);
    });

    let rawLow = 0;
    let rawNormal = 0;
    buildings.forEach((b) => {
        rawLow += Number(b.low.toFixed(2));
        rawNormal += Number(b.standard.toFixed(2));
    });

    const low = roundedBasedOn33Percent(rawLow);
    const standard = roundedBasedOn33Percent(rawNormal);
    const total = low + standard;
    if (total <= 0) return { low: 0, standard: 0, total: 0 };
    return { low, standard, total };
}

/** irrPro is 0–100 from shade. Never invent 100% / 80% when shade has not run. */
function solarAccessFromMesh(mesh: any, irrProRaw?: number): number {
    const irrPro = irrProRaw ?? Number(mesh?.irrPro);
    if (Number.isFinite(irrPro) && irrPro > 1 && irrPro <= 100) return Math.round(irrPro);
    const ptHash = Number(mesh?.ptHash);
    if (Number.isFinite(ptHash) && ptHash > 1 && ptHash <= 100) return Math.round(ptHash);
    return 0;
}

function sleepMs(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function pciModuleSolarValues(viewer: Viewer | null | undefined): number[] {
    return (viewer?.moduleMeshArr ?? [])
        .map((mesh: any) => solarAccessFromMesh(mesh))
        .filter((value) => value > 0);
}

/** True when shade has produced real per-module access (not the default-100 placeholder). */
export function isPciShadeReady(viewer: Viewer | null | undefined): boolean {
    const state = useViewerStore.getState() as {
        refreshingShade?: boolean;
        actualWattage?: number;
        efficiency?: number;
    };
    if (state.refreshingShade) return false;
    const values = pciModuleSolarValues(viewer);
    if (!values.length) return false;
    const hasVariation = values.some((value) => value < 99);
    const tsrf = Number(state.efficiency) || 0;
    const production = Number(state.actualWattage) || 0;
    return hasVariation || (production > 0 && tsrf > 0);
}

async function waitWhile(predicate: () => boolean, timeoutMs: number): Promise<boolean> {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
        if (!predicate()) return true;
        await sleepMs(350);
    }
    return !predicate();
}

/** Wait for shade worker + SetModuleGraph so irrPro / multipliers are real. */
export async function waitForPciShadeAndGraph(viewer: Viewer | null | undefined): Promise<boolean> {
    if (!viewer) return false;
    const epoch = getShadeRunGeneration();
    const stillCurrent = () => getShadeRunGeneration() === epoch;
    const state = () => useViewerStore.getState() as { refreshingShade?: boolean };

    await waitWhile(() => stillCurrent() && Boolean(state().refreshingShade), 60000);
    if (!stillCurrent()) return false;
    try {
        await SetModuleGraph(viewer);
    } catch {
        /* continue */
    }
    if (isPciShadeReady(viewer)) return true;
    if (!stillCurrent()) return false;

    try {
        viewer.refreshShade?.();
    } catch {
        /* ignore */
    }
    await waitWhile(() => stillCurrent() && !state().refreshingShade, 8000);
    if (!stillCurrent()) return false;
    await waitWhile(() => stillCurrent() && Boolean(state().refreshingShade), 90000);
    if (!stillCurrent()) return false;
    try {
        await SetModuleGraph(viewer);
    } catch {
        /* ignore */
    }
    return isPciShadeReady(viewer) || (viewer.moduleMeshArr?.length ?? 0) > 0;
}

/** Serialize loaded viewer roofs for the pre-contract Design drawer. */
export function collectPciSiteRoofs(viewer: Viewer | null | undefined): PciSiteRoof[] {
    if (!viewer?.roofMeshArr?.length) return [];

    const seen = new Set<number>();
    const roofs: PciSiteRoof[] = [];
    const squareLookup = roofSquareLookupFromViewer();

    viewer.roofMeshArr.forEach((mesh: any) => {
        if (isSmallFace(mesh)) return;
        const roofTagId = Number(mesh.userData?.roof_tag_id);
        if (!Number.isFinite(roofTagId) || seen.has(roofTagId)) return;
        seen.add(roofTagId);
        const name = mesh.userData?.nameOverride ?? roofTagId;
        const fromTable = squareLookup.get(roofTagId);
        const fromMesh = squaresFromMeshUserData(mesh);
        roofs.push({
            roofFaceId: String(mesh.roofFaceId ?? ''),
            roofTagId,
            label: `Roof ${name}`,
            buildingName: mesh.userData?.buildingName ? String(mesh.userData.buildingName) : undefined,
            lowSlopeSquare: fromTable ? fromTable.low : fromMesh.low,
            normalSlopeSquare: fromTable ? fromTable.normal : fromMesh.normal,
        });
    });

    return roofs.sort((a, b) => a.roofTagId - b.roofTagId);
}

function medianPositiveGap(sorted: number[]): number {
    const gaps: number[] = [];
    for (let i = 1; i < sorted.length; i += 1) {
        const gap = sorted[i] - sorted[i - 1];
        if (gap > 1e-5) gaps.push(gap);
    }
    if (!gaps.length) return 1;
    gaps.sort((a, b) => a - b);
    return gaps[Math.floor(gaps.length / 2)] || 1;
}

function clusterAxis(values: number[], tolerance: number): number[] {
    if (!values.length) return [];
    const sorted = [...values].sort((a, b) => a - b);
    const clusters: number[] = [sorted[0]];
    for (let i = 1; i < sorted.length; i += 1) {
        const last = clusters[clusters.length - 1];
        if (Math.abs(sorted[i] - last) > tolerance) {
            clusters.push(sorted[i]);
        } else {
            clusters[clusters.length - 1] = (last + sorted[i]) / 2;
        }
    }
    return clusters;
}

function nearestClusterIndex(value: number, clusters: number[]): number {
    let best = 0;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < clusters.length; i += 1) {
        const dist = Math.abs(clusters[i] - value);
        if (dist < bestDist) {
            bestDist = dist;
            best = i;
        }
    }
    return best;
}

type PciViewCamera = {
    updateMatrixWorld?: (force?: boolean) => void;
} | null | undefined;

function moduleLocalX(mesh: any): number {
    return Number(mesh?.position?.x) || 0;
}

function moduleLocalY(mesh: any): number {
    return Number(mesh?.position?.y) || 0;
}

/** Project a module into camera NDC so board left/top matches the 3D viewport. */
function projectMeshToView(
    mesh: any,
    camera: PciViewCamera,
): { x: number; y: number } | null {
    if (!camera || typeof mesh?.getWorldPosition !== 'function') return null;
    try {
        camera.updateMatrixWorld?.(true);
        mesh.updateWorldMatrix?.(true, false);
        const ndc = mesh.getWorldPosition(new Vector3()).project(camera as never);
        if (!Number.isFinite(ndc.x) || !Number.isFinite(ndc.y)) return null;
        return { x: ndc.x, y: ndc.y };
    } catch {
        return null;
    }
}

/**
 * True when larger local values appear further right / up on screen.
 * Roof-local +X often points opposite the aerial view (south faces look mirrored).
 */
function localAxisFollowsView(
    meshes: any[],
    camera: PciViewCamera,
    localOf: (mesh: any) => number,
    viewOf: (ndc: { x: number; y: number }) => number,
): boolean | null {
    if (!camera || meshes.length < 2) return null;
    let minMesh = meshes[0];
    let maxMesh = meshes[0];
    let minLocal = localOf(meshes[0]);
    let maxLocal = minLocal;
    meshes.forEach((mesh) => {
        const local = localOf(mesh);
        if (local < minLocal) {
            minLocal = local;
            minMesh = mesh;
        }
        if (local > maxLocal) {
            maxLocal = local;
            maxMesh = mesh;
        }
    });
    if (Math.abs(maxLocal - minLocal) < 1e-5) return null;
    const minView = projectMeshToView(minMesh, camera);
    const maxView = projectMeshToView(maxMesh, camera);
    if (!minView || !maxView) return null;
    const delta = viewOf(maxView) - viewOf(minView);
    if (Math.abs(delta) < 1e-5) return null;
    return delta > 0;
}

function meshIdOf(mesh: any): string {
    const id = mesh?.moduleId;
    return id == null ? '' : String(id).trim();
}

/** Assign R#M# ids from roof geometry only — must not change when the camera orbits. */
function stampStablePciCellIds(meshes: any[], roofTagId: number): void {
    if (!meshes.length) return;

    let maxPresent = 0;
    meshes.forEach((mesh) => {
        const raw = String(mesh?.userData?.pciCellId ?? '').trim();
        const match = raw.match(/^R\d+M(\d+)$/i);
        if (match) maxPresent = Math.max(maxPresent, Number(match[1]) || 0);
    });

    const needsId = meshes.filter((mesh) => {
        const raw = String(mesh?.userData?.pciCellId ?? '').trim();
        return !raw || !/^R\d+M\d+$/i.test(raw);
    });
    if (needsId.length === 0) return;

    const sorted = [...needsId].sort((a, b) => {
        const dy = moduleLocalY(b) - moduleLocalY(a);
        if (Math.abs(dy) > 1e-4) return dy;
        return moduleLocalX(a) - moduleLocalX(b);
    });

    sorted.forEach((mesh) => {
        maxPresent += 1;
        mesh.userData = { ...(mesh.userData ?? {}), pciCellId: `R${roofTagId}M${maxPresent}` };
    });
}

/**
 * Build a row-major face grid from live module local positions (x / y).
 * Column/row order is flipped to match the aerial camera so left on the board is left in 3D.
 * Gaps become null cells so PreContract can render the real Aurora layout.
 */
export function buildFaceGridFromModuleMeshes(
    meshes: any[],
    roofTagId: number,
    camera?: PciViewCamera,
): {
    cols: number;
    modules: PreContractModuleCell[];
    moduleOrientation: 'portrait' | 'landscape';
} {
    if (!meshes.length) {
        return { cols: 0, modules: [], moduleOrientation: 'portrait' };
    }

    const portCount = meshes.filter((m) => m?.dir === 'port' || m?.dir === 'both').length;
    const moduleOrientation: 'portrait' | 'landscape' =
        portCount >= meshes.length / 2 ? 'portrait' : 'landscape';

    stampStablePciCellIds(meshes, roofTagId);

    const xs = meshes.map(moduleLocalX);
    const zs = meshes.map(moduleLocalY);
    const sortedX = [...xs].sort((a, b) => a - b);
    const sortedZ = [...zs].sort((a, b) => a - b);
    const tolX = Math.max(medianPositiveGap(sortedX) * 0.4, 1e-3);
    const tolZ = Math.max(medianPositiveGap(sortedZ) * 0.4, 1e-3);
    let colCenters = clusterAxis(xs, tolX);
    // Descending local Y so first row is the "top" of the roof face; camera may reverse this.
    let rowCenters = clusterAxis(zs, tolZ).sort((a, b) => b - a);

    const xFollowsRight = localAxisFollowsView(meshes, camera, moduleLocalX, (ndc) => ndc.x);
    if (xFollowsRight === false) colCenters = [...colCenters].reverse();
    const yFollowsUp = localAxisFollowsView(meshes, camera, moduleLocalY, (ndc) => ndc.y);
    if (yFollowsUp === false) rowCenters = [...rowCenters].reverse();

    const cols = Math.max(1, colCenters.length);
    const rows = Math.max(1, rowCenters.length);
    const gridMeshes: Array<any | null> = Array.from({ length: rows * cols }, () => null);

    meshes.forEach((mesh) => {
        const col = nearestClusterIndex(moduleLocalX(mesh), colCenters);
        const row = nearestClusterIndex(moduleLocalY(mesh), rowCenters);
        const index = row * cols + col;
        if (index < 0 || index >= gridMeshes.length) return;
        gridMeshes[index] = mesh;
    });

    const modules: PreContractModuleCell[] = gridMeshes.map((mesh) => {
        if (!mesh) return null;
        const id = String(mesh.userData?.pciCellId ?? '').trim() || meshIdOf(mesh);
        const meshId = meshIdOf(mesh);
        const irrPro = Number(mesh.irrPro);
        const solarAccess = solarAccessFromMesh(mesh, irrPro);
        const multiplier = Number(mesh.multiplier);
        const production = Number(mesh.production);
        const shadeColor = preContractIrrColorToCss(Number((mesh as { irrColor?: number }).irrColor));
        return {
            id,
            ...(meshId ? { meshId } : {}),
            solarAccess,
            included: true,
            ...(Number.isFinite(multiplier) && multiplier > 0 ? { multiplier } : {}),
            ...(Number.isFinite(production) && production > 0 ? { productionKwh: Math.round(production) } : {}),
            ...(shadeColor ? { shadeColor } : {}),
        };
    });

    return { cols, modules, moduleOrientation };
}

/** Live module boards aligned to PCI roof list (same order as collectPciSiteRoofs). */
export function collectPciRoofModuleLayouts(
    viewer: Viewer | null | undefined,
): PreContractRoofModuleLayout[] {
    const roofs = collectPciSiteRoofs(viewer);
    if (!viewer?.roofMeshArr?.length || !roofs.length) return [];

    return roofs.map((roofMeta, roofIndex) => {
        const roofMesh =
            viewer.roofMeshArr.find(
                (mesh: any) => Number(mesh?.userData?.roof_tag_id) === roofMeta.roofTagId,
            ) ?? null;
        const meshes = roofMesh ? getRoofModuleMeshes(roofMesh) : [];
        const built = buildFaceGridFromModuleMeshes(meshes, roofMeta.roofTagId, viewer.camera);
        return {
            roofIndex,
            roofTagId: roofMeta.roofTagId,
            label: roofMeta.label,
            cols: built.cols,
            modules: built.modules,
            moduleOrientation: built.moduleOrientation,
        };
    });
}

/**
 * Apply roof include chips onto live layouts without inventing blueprint modules.
 *
 * Only clears the modules of roofs that are off. Roof selections are derived from
 * per-module includes, so forcing `included: true` on a selected roof would wipe
 * the module toggles that produced the selection in the first place.
 */
export function applyRoofSelectionsToLayouts(
    layouts: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): PreContractRoofModuleLayout[] {
    return layouts.map((layout, index) => {
        if (roofSelections[index] !== false) return layout;
        if (!layout.modules.some((cell) => cell?.included)) return layout;
        return {
            ...layout,
            modules: layout.modules.map((cell) => (cell ? { ...cell, included: false } : null)),
        };
    });
}

function layoutMergeKey(layout: PreContractRoofModuleLayout): string {
    if (typeof layout.roofTagId === 'number' && Number.isFinite(layout.roofTagId)) {
        return `tag:${layout.roofTagId}`;
    }
    return `idx:${layout.roofIndex}`;
}

/** Keep include toggles; take newer solar access / production from the live embed snapshot. */
export function mergeLiveModuleLayouts(
    prev: PreContractRoofModuleLayout[],
    live: PreContractRoofModuleLayout[],
    roofSelections: boolean[],
): PreContractRoofModuleLayout[] {
    if (!live.length) {
        return applyRoofSelectionsToLayouts(prev, roofSelections);
    }
    if (!prev.length) {
        return applyRoofSelectionsToLayouts(live, roofSelections);
    }

    const prevByKey = new Map<string, PreContractRoofModuleLayout>();
    prev.forEach((layout) => {
        prevByKey.set(layoutMergeKey(layout), layout);
        prevByKey.set(`idx:${layout.roofIndex}`, layout);
    });

    return live.map((layout, index) => {
        const prevLayout =
            prevByKey.get(layoutMergeKey(layout)) ??
            prevByKey.get(`idx:${layout.roofIndex}`) ??
            prev[index];
        // The viewer drops meshes for roofs it is not currently rendering. Keep the panels
        // the board already knows about so the roof stays selectable instead of emptying.
        if (
            !layout.modules.some((cell) => cell != null) &&
            prevLayout?.modules.some((cell) => cell != null)
        ) {
            return {
                ...layout,
                cols: prevLayout.cols,
                modules: prevLayout.modules,
                moduleOrientation: prevLayout.moduleOrientation ?? layout.moduleOrientation,
            };
        }
        const prevById = new Map<string, NonNullable<PreContractModuleCell>>();
        const prevByMeshId = new Map<string, NonNullable<PreContractModuleCell>>();
        prevLayout?.modules.forEach((cell) => {
            if (!cell) return;
            prevById.set(String(cell.id).trim().toUpperCase(), cell);
            if (cell.meshId) prevByMeshId.set(String(cell.meshId).trim().toUpperCase(), cell);
        });
        const prevPresent = prevLayout?.modules.filter((cell) => cell != null).length ?? 0;
        const livePresent = layout.modules.filter((cell) => cell != null).length;
        // A max-fill scene still has every mesh. Keep the working board and only
        // copy shade/production onto panels we already had.
        if (prevLayout && prevPresent > 0 && livePresent > prevPresent) {
            const liveById = new Map<string, NonNullable<PreContractModuleCell>>();
            const liveByMeshId = new Map<string, NonNullable<PreContractModuleCell>>();
            layout.modules.forEach((cell) => {
                if (!cell) return;
                liveById.set(String(cell.id).trim().toUpperCase(), cell);
                if (cell.meshId) {
                    liveByMeshId.set(String(cell.meshId).trim().toUpperCase(), cell);
                }
            });
            return {
                ...prevLayout,
                modules: prevLayout.modules.map((cell) => {
                    if (!cell) return null;
                    const meshKey = cell.meshId ? String(cell.meshId).trim().toUpperCase() : '';
                    const liveCell =
                        (meshKey ? liveByMeshId.get(meshKey) : undefined) ??
                        liveById.get(String(cell.id).trim().toUpperCase()) ??
                        null;
                    if (!liveCell) return cell;
                    const solarAccess =
                        liveCell.solarAccess > 0 ? liveCell.solarAccess : cell.solarAccess;
                    const productionKwh =
                        typeof liveCell.productionKwh === 'number' && liveCell.productionKwh > 0
                            ? liveCell.productionKwh
                            : cell.productionKwh;
                    const multiplier =
                        typeof liveCell.multiplier === 'number' && liveCell.multiplier > 0
                            ? liveCell.multiplier
                            : cell.multiplier;
                    const shadeColor = liveCell.shadeColor ?? cell.shadeColor;
                    return {
                        ...cell,
                        solarAccess,
                        ...(typeof multiplier === 'number' && multiplier > 0 ? { multiplier } : {}),
                        ...(typeof productionKwh === 'number' && productionKwh > 0
                            ? { productionKwh }
                            : {}),
                        ...(shadeColor ? { shadeColor } : {}),
                    };
                }),
            };
        }
        return {
            ...layout,
            modules: layout.modules.map((cell, cellIndex) => {
                if (!cell) return null;
                const meshKey = cell.meshId ? String(cell.meshId).trim().toUpperCase() : '';
                const old =
                    (meshKey ? prevByMeshId.get(meshKey) : undefined) ??
                    prevById.get(String(cell.id).trim().toUpperCase()) ??
                    (meshKey ? undefined : prevLayout?.modules[cellIndex]) ??
                    null;
                const solarAccess =
                    cell.solarAccess > 0 ? cell.solarAccess : old?.solarAccess ?? cell.solarAccess;
                const productionKwh =
                    typeof cell.productionKwh === 'number' && cell.productionKwh > 0
                        ? cell.productionKwh
                        : old?.productionKwh;
                const multiplier =
                    typeof cell.multiplier === 'number' && cell.multiplier > 0
                        ? cell.multiplier
                        : old?.multiplier;
                const shadeColor = cell.shadeColor ?? old?.shadeColor;
                return {
                    ...cell,
                    // Unknown meshes are the leftover max-fill, not newly selected panels.
                    included: old ? old.included : false,
                    solarAccess,
                    ...(typeof multiplier === 'number' && multiplier > 0 ? { multiplier } : {}),
                    ...(typeof productionKwh === 'number' && productionKwh > 0
                        ? { productionKwh }
                        : {}),
                    ...(shadeColor ? { shadeColor } : {}),
                };
            }),
        };
    });
}

function pciModuleCellEqual(
    a: PreContractModuleCell | null,
    b: PreContractModuleCell | null,
): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
        a.id === b.id &&
        a.included === b.included &&
        a.solarAccess === b.solarAccess &&
        a.meshId === b.meshId &&
        a.multiplier === b.multiplier &&
        a.productionKwh === b.productionKwh &&
        a.shadeColor === b.shadeColor
    );
}

/** Skip parent re-renders when the embed republishes unchanged roof/module grids. */
export function pciRoofModuleLayoutsEqual(
    a: PreContractRoofModuleLayout[],
    b: PreContractRoofModuleLayout[],
): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((layout, index) => {
        const other = b[index];
        if (!other) return false;
        if (
            layout.roofIndex !== other.roofIndex ||
            layout.roofTagId !== other.roofTagId ||
            layout.label !== other.label ||
            layout.cols !== other.cols ||
            layout.moduleOrientation !== other.moduleOrientation ||
            layout.modules.length !== other.modules.length
        ) {
            return false;
        }
        return layout.modules.every((cell, cellIndex) =>
            pciModuleCellEqual(cell, other.modules[cellIndex] ?? null),
        );
    });
}

export function pciSiteRoofsEqual(a: PciSiteRoof[], b: PciSiteRoof[]): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((roof, index) => {
        const other = b[index];
        return (
            other != null &&
            roof.roofFaceId === other.roofFaceId &&
            roof.roofTagId === other.roofTagId &&
            roof.label === other.label &&
            roof.buildingName === other.buildingName &&
            roof.lowSlopeSquare === other.lowSlopeSquare &&
            roof.normalSlopeSquare === other.normalSlopeSquare
        );
    });
}

/** Tell the pre-contract parent that the viewport embed failed to load site model data. */
export function postPciSiteLoadErrorToParent(message: string): void {
    if (!useViewerStore.getState().isViewportOnlyEmbed) return;
    if (typeof window === 'undefined' || window.parent === window) return;
    const text = message.trim();
    if (!text) return;
    try {
        window.parent.postMessage(
            { type: PCI_ONEDRAW_LOAD_ERROR_MSG, message: text },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

/**
 * Publish Finance Partner Eligibility scene values so the pre-contract panel shows the
 * same tilt / racking / installation reads as oneDRAW, which owns the 3D scene.
 */
export function postPciEligibilityValuesToParent(viewer: Viewer | null | undefined): void {
    if (!useViewerStore.getState().isViewportOnlyEmbed) return;
    if (typeof window === 'undefined' || window.parent === window) return;

    const store = useViewerStore.getState();
    const values = collectFinanceEligibilityCurrentValues({
        sceneViewer: (viewer as any) ?? {},
        roofInfoArr: store.roofInfoArr,
        roofPitchOverrideArr: store.roofPitchOverrideArr,
        multiplier: store.multiplier,
        totalSystemSize: store.totalSystemSize,
        actualWattage: store.actualWattage,
        utilityWattage: store.utilityWattage,
        annualUsageKwh: store.SFannualUsage ?? null,
        moduleCount: Number(store.modeCount) || 0,
        hasBattery: false,
        detachedStructureBuildingIds: store.detachedStructureBuildingIds,
    });

    const scene = pickFinanceEligibilitySceneValues(values);

    try {
        window.parent.postMessage(
            { type: PCI_ONEDRAW_ELIGIBILITY_MSG, eligibility: scene },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

export function postPciSiteRoofsToParent(viewer: Viewer | null | undefined): void {
    if (!useViewerStore.getState().isViewportOnlyEmbed) return;
    if (typeof window === 'undefined' || window.parent === window) return;

    postPciEligibilityValuesToParent(viewer);

    const roofs = collectPciSiteRoofs(viewer);
    if (!roofs.length) return;
    const layouts = collectPciRoofModuleLayouts(viewer);
    const trees = collectPciSiteTrees(viewer);
    const moduleCount = layouts.reduce(
        (sum, layout) => sum + layout.modules.filter(Boolean).length,
        0,
    );
    const shadeReady = isPciShadeReady(viewer);

    ensurePciTreeIdLabels(viewer);

    try {
        window.parent.postMessage(
            {
                type: PCI_ONEDRAW_ROOFS_MSG,
                roofs,
                layouts,
                trees,
                moduleCount,
                shadeReady,
                // Per-module production comes from multipliers fetched for this vendor
                // (GetFaceProdAPI), so the parent must not assume its own selection.
                lossesVendor: normalizeLossesVendor(
                    useViewerStore.getState().selectedLossesVendor,
                ),
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

const PCI_ROOF_PIN_COLORS = [
    '#1d4ed8',
    '#0f766e',
    '#b45309',
    '#7c3aed',
    '#be123c',
    '#0369a1',
    '#15803d',
    '#c2410c',
];

function pciRoofPinColor(tag: number): string {
    const index = Math.abs(Number.isFinite(tag) ? tag : 0) % PCI_ROOF_PIN_COLORS.length;
    return PCI_ROOF_PIN_COLORS[index];
}

function pciRoofPinMeshes(viewer: Viewer): any[] {
    return (viewer.roofMeshArr ?? []).filter((mesh: any) => {
        if (viewer.checkIfRoofIsSmallFace?.(mesh)) return false;
        const tag = Number(mesh.userData?.roof_tag_id);
        return Number.isFinite(tag);
    });
}

function pciRoofPinLabel(mesh: any): string {
    const override = mesh?.userData?.nameOverride;
    if (override != null && String(override).trim() !== '') return String(override);
    return String(mesh?.userData?.roof_tag_id ?? '');
}

function pciRoofPinSignature(meshes: any[]): string {
    return meshes
        .map((mesh) => pciRoofPinLabel(mesh))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .join('|');
}

function clearPciRoofPins(group: { children: any[]; clear: () => void }): void {
    group.children.forEach((child: any) => {
        child.element?.remove?.();
    });
    group.clear();
}

function syncPciRoofPinPositions(viewer: Viewer, group: { children: any[] }): void {
    const byTag = new Map<string, any>();
    group.children.forEach((child: any) => {
        const key = String(child.userData?.pciRoofPin ?? '');
        if (key) byTag.set(key, child);
    });
    pciRoofPinMeshes(viewer).forEach((mesh) => {
        const key = pciRoofPinLabel(mesh);
        const pin = byTag.get(key);
        if (!pin) return;
        const center = getCenterPoint(mesh);
        pin.position.set(center.x, center.y + 0.9, center.z);
    });
}

function applyPciRoofPinInclusion(group: { children: any[] }, includedRoofTagIds?: number[]): void {
    const included =
        includedRoofTagIds == null
            ? null
            : new Set(includedRoofTagIds.map((id) => Number(id)));
    group.children.forEach((child: any) => {
        const el = child.element as HTMLElement | undefined;
        if (!el) return;
        const tag = Number(child.userData?.roofTagId);
        const on = included == null || included.has(tag);
        el.classList.toggle('pci-roof-pin--off', !on);
    });
}

/**
 * Stable CSS2D roof pins (no SVG texture reload, so they don't flash).
 * Rebuilds only when the roof identity set changes; otherwise updates pose + included state.
 */
export function ensurePciRoofIdTags(
    viewer: Viewer | null | undefined,
    includedRoofTagIds?: number[],
): void {
    if (!viewer?.roofIDNumberGroup || !viewer.roofMeshArr?.length) return;

    try {
        viewer.toggleRoofIDTriangle?.(false);
    } catch {
        /* ignore */
    }

    const group = viewer.roofIDNumberGroup as any;
    const meshes = pciRoofPinMeshes(viewer);
    const signature = pciRoofPinSignature(meshes);
    const pinsIntact =
        group.userData?.pciRoofPinSignature === signature &&
        group.children.length === meshes.length &&
        group.children.every((child: any) => child.userData?.pciRoofPin);

    if (!pinsIntact) {
        clearPciRoofPins(group);
        meshes.forEach((mesh) => {
            const tag = Number(mesh.userData?.roof_tag_id);
            const label = pciRoofPinLabel(mesh);
            const color = pciRoofPinColor(Number.isFinite(tag) ? tag : 0);
            const el = document.createElement('div');
            el.className = 'pci-roof-pin';
            el.style.setProperty('--pci-roof-pin', color);
            const badge = document.createElement('span');
            badge.className = 'pci-roof-pin__badge';
            badge.textContent = label;
            const stem = document.createElement('span');
            stem.className = 'pci-roof-pin__stem';
            stem.setAttribute('aria-hidden', 'true');
            el.append(badge, stem);
            const pin = new CSS2DObject(el);
            pin.userData = { pciRoofPin: label, roofTagId: tag };
            const center = getCenterPoint(mesh);
            pin.position.set(center.x, center.y + 0.9, center.z);
            group.add(pin);
        });
        group.userData = { ...(group.userData ?? {}), pciRoofPinSignature: signature };
    } else {
        syncPciRoofPinPositions(viewer, group);
    }

    group.visible = true;
    applyPciRoofPinInclusion(group, includedRoofTagIds);
}

/** Highlight included roofs + keep stable color pins; dim excluded. */
export function applyPciRoofSelectionHighlight(
    viewer: Viewer | null | undefined,
    includedRoofTagIds: number[],
): void {
    if (!viewer?.roofMeshArr?.length) return;

    const included = new Set(includedRoofTagIds.map((id) => Number(id)));
    ensurePciRoofIdTags(viewer, includedRoofTagIds);

    viewer.roofMeshArr.forEach((mesh: any) => {
        const tag = Number(mesh.userData?.roof_tag_id);
        const on = included.has(tag);
        const oriColor = mesh.oriColor;
        const oriMap = mesh.oriMap;
        const mat = mesh.material;
        if (!mat) return;

        if (on) {
            mat.map = undefined;
            if (typeof mat.color?.setHex === 'function') mat.color.setHex(0x8ac7db);
            mat.opacity = 1;
            mat.transparent = false;
        } else {
            if (typeof mat.color?.setHex === 'function' && oriColor != null) {
                mat.color.setHex(oriColor);
            }
            mat.map = oriMap;
            mat.opacity = 0.32;
            mat.transparent = true;
        }
        mat.needsUpdate = true;
    });
    try {
        viewer.render?.();
    } catch {
        /* ignore */
    }
}

export function postPciRoofSelectionToEmbed(includedRoofTagIds: number[]): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(
            {
                type: PCI_ONEDRAW_ROOF_SELECTION_MSG,
                includedRoofTagIds,
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

function pciModuleKeys(mesh: any): string[] {
    const keys = new Set<string>();
    const stamped =
        typeof mesh?.userData?.pciCellId === 'string' ? mesh.userData.pciCellId.trim().toUpperCase() : '';
    if (stamped) keys.add(stamped);
    const meshId = meshIdOf(mesh).toUpperCase();
    if (meshId) keys.add(meshId);
    return [...keys];
}

function meshMatchesIncluded(mesh: any, included: Set<string>): boolean {
    return pciModuleKeys(mesh).some((key) => included.has(key));
}

function ensureViewerPciCellIdsStamped(viewer: Viewer | null | undefined): void {
    if (!viewer?.roofMeshArr?.length) return;
    viewer.roofMeshArr.forEach((roofMesh: any) => {
        if (roofMesh?.disableModule) return;
        const roofTagId = Number(roofMesh?.userData?.roof_tag_id);
        if (!Number.isFinite(roofTagId)) return;
        stampStablePciCellIds(getRoofModuleMeshes(roofMesh), roofTagId);
    });
}

/** Module id → board cell id for every stamped module currently in the scene. */
function collectPciCellIdStamps(viewer: Viewer | null | undefined): Map<string, string> {
    const stamps = new Map<string, string>();
    viewer?.roofMeshArr?.forEach((roofMesh: any) => {
        getRoofModuleMeshes(roofMesh).forEach((mesh: any) => {
            const stamp = String(mesh?.userData?.pciCellId ?? '').trim();
            const moduleId = meshIdOf(mesh).toUpperCase();
            if (stamp && moduleId) stamps.set(moduleId, stamp);
        });
    });
    return stamps;
}

/**
 * Put board cell ids back on meshes re-created by a snapshot restore. Restores keep
 * `moduleId` but drop userData, so without this the next stamp pass renumbers whatever
 * survived and the board ids no longer name the same panels.
 */
function applyPciCellIdStamps(
    viewer: Viewer | null | undefined,
    stamps: Map<string, string>,
): void {
    if (stamps.size === 0) return;
    viewer?.roofMeshArr?.forEach((roofMesh: any) => {
        getRoofModuleMeshes(roofMesh).forEach((mesh: any) => {
            const stamp = stamps.get(meshIdOf(mesh).toUpperCase());
            if (!stamp) return;
            mesh.userData = { ...(mesh.userData ?? {}), pciCellId: stamp };
        });
    });
}

/** Show/hide live modules in the embed to match PreContract include toggles. */
export function applyPciModuleSelectionHighlight(
    viewer: Viewer | null | undefined,
    includedModuleIds: string[],
): void {
    if (!viewer?.moduleMeshArr?.length) return;
    ensureViewerPciCellIdsStamped(viewer);
    const included = new Set(
        includedModuleIds.map((id) => String(id).trim().toUpperCase()).filter(Boolean),
    );
    const matchCount = viewer.moduleMeshArr.filter((mesh: any) =>
        meshMatchesIncluded(mesh, included),
    ).length;
    // Empty list clears selection once layouts exist. Non-empty with zero matches means ids
    // are not stamped yet — leave visibility unchanged instead of showing every panel.
    if (included.size > 0 && matchCount === 0) return;
    const filterActive = included.size === 0 || matchCount > 0;

    viewer.moduleMeshArr.forEach((mesh: any) => {
        const on = filterActive ? meshMatchesIncluded(mesh, included) : true;
        mesh.visible = on;
        if (mesh.material) {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mats.forEach((mat: any) => {
                if (!mat) return;
                mat.transparent = !on;
                mat.opacity = on ? 1 : 0.18;
                mat.needsUpdate = true;
            });
        }
    });
    try {
        viewer.render?.();
    } catch {
        /* ignore */
    }
}

/** Undo a trimmed selection so every live module is visible again. */
export function applyPciShowAllModules(viewer: Viewer | null | undefined): void {
    if (!viewer?.moduleMeshArr?.length) return;
    viewer.moduleMeshArr.forEach((mesh: any) => {
        mesh.visible = true;
        if (!mesh.material) return;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach((mat: any) => {
            if (!mat) return;
            mat.transparent = false;
            mat.opacity = 1;
            mat.needsUpdate = true;
        });
    });
    try {
        viewer.render?.();
    } catch {
        /* ignore */
    }
}

export function postPciModuleSelectionToEmbed(includedModuleIds: string[]): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(
            {
                type: PCI_ONEDRAW_MODULE_SELECTION_MSG,
                includedModuleIds,
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

/**
 * Frame the pre-contract viewport on the building/roofs (not the full yard/trees).
 * `top` = near top-down default embed view; `3d` = orbitable popup framing.
 */
export function fitPciCameraToBuilding(
    viewer: Viewer | null | undefined,
    mode: 'top' | '3d' = 'top',
): void {
    if (!viewer?.camera || !viewer?.controls || !viewer.roofGroup) return;
    try {
        if (mode === 'top') {
            SetTopFitView(viewer, {
                includeRoofs: true,
                includeTrees: false,
                includeObstructions: false,
                includeMapCustom: false,
                padding: 1.25,
                minZoom: 2,
                maxZoom: 22,
                height: 110,
            });
            viewer.render?.();
            return;
        }

        const box = new Box3().setFromObject(viewer.roofGroup);
        if (box.isEmpty()) return;
        const center = box.getCenter(new Vector3());
        const size = box.getSize(new Vector3());
        const maxDim = Math.max(size.x, size.z) || 1;
        const camWidth = viewer.camera.right - viewer.camera.left;
        const zoom = Math.max(3, Math.min(22, camWidth / (maxDim * 1.4)));
        const dist = Math.max(48, maxDim * 0.95);

        viewer.controls.maxPolarAngle = Math.PI / 2 - 0.1;
        viewer.camera.position.set(center.x, 90, center.z + dist);
        viewer.controls.target.set(center.x, center.y, center.z);
        viewer.camera.zoom = zoom;
        viewer.camera.updateProjectionMatrix();
        viewer.controls.update();
        viewer.render?.();
    } catch {
        /* ignore */
    }
}

/** Orbit camera for the pre-contract 3D popup. Does not change include/exclude. */
export function preparePci3dView(viewer: Viewer | null | undefined): void {
    if (!viewer) return;
    try {
        viewer.setVisibleGroup?.('modules', true);
    } catch {
        /* ignore */
    }
    const siteReady = Boolean(useViewerStore.getState().loadingCompleted);
    if (siteReady && !viewer.moduleMeshArr?.length && viewer.roofMeshArr?.length) {
        try {
            addModulesToAllRoofsMaxFill(viewer, null, 'port');
        } catch {
            /* ignore */
        }
    }
    try {
        viewer.setTopView?.(false);
    } catch {
        /* ignore */
    }
    try {
        SetCameraView(viewer, '3d', true);
    } catch {
        /* ignore */
    }
    fitPciCameraToBuilding(viewer, '3d');
    try {
        if (viewer.controls) viewer.controls.enableRotate = true;
    } catch {
        /* ignore */
    }
    try {
        viewer.render?.();
    } catch {
        /* ignore */
    }
}

export function postPci3dViewToEmbed(): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage({ type: PCI_ONEDRAW_3D_VIEW_MSG }, window.location.origin);
    } catch {
        /* ignore */
    }
}

/** Ask the shared background oneDRAW instance to open Save Design / Finalize. */
export function postPciFinalizeToEmbed(): void {
    postPciEmbedMessage({ type: PCI_ONEDRAW_FINALIZE_MSG });
}

function postPciEmbedMessage(payload: Record<string, unknown>): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(payload, window.location.origin);
    } catch {
        /* ignore */
    }
}

export function postPciFinalizeSnapshotRequest(): void {
    postPciEmbedMessage({ type: PCI_ONEDRAW_FINALIZE_SNAPSHOT_MSG });
}

export function requestPciFinalizeSaveFromEmbed(
    input: PciFinalizeSaveInput,
    timeoutMs = 180_000,
): Promise<PciFinalizeSaveResult> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve({
            ok: false,
            title: 'Save Failed',
            message: 'Finalize is unavailable in this environment.',
        });
    }
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    if (!frame?.contentWindow) {
        return Promise.resolve({
            ok: false,
            title: 'Save Failed',
            message: 'The background oneDRAW instance is not ready.',
        });
    }
    const requestId = `pci-finalize-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new Promise((resolve) => {
        let settled = false;
        const finish = (result: PciFinalizeSaveResult) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(result);
        };
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as (PciFinalizeSaveResult & { type?: string; requestId?: string }) | null;
            if (data?.type !== PCI_ONEDRAW_FINALIZE_SAVE_DONE_MSG) return;
            if (data.requestId !== requestId) return;
            finish({
                ok: data.ok === true,
                title: data.title || (data.ok ? 'Design Saved Successfully' : 'Save Failed'),
                message: data.message || '',
                needsEfficiencyOverride: data.needsEfficiencyOverride,
                efficiency: data.efficiency,
                solarAccess: data.solarAccess,
            });
        };
        const timer = window.setTimeout(
            () =>
                finish({
                    ok: false,
                    title: 'Save Failed',
                    message: 'Finalize timed out. Try again after shade and production finish.',
                }),
            timeoutMs,
        );
        window.addEventListener('message', onMessage);
        postPciEmbedMessage({
            type: PCI_ONEDRAW_FINALIZE_SAVE_MSG,
            requestId,
            ...input,
        });
    });
}

export function collectPciSiteTrees(viewer: Viewer | null | undefined): PciSiteTree[] {
    const children = viewer?.treeGroup?.children ?? [];
    return children
        .map((tree: any) => {
            const id = String(tree?.userData?.treeId ?? tree?.obstId ?? '').trim();
            if (!id) return null;
            const liveImpact = Number(tree.treeImpact);
            const ghostImpact = Number(tree.userData?.impactAtGhost);
            const multiplier = tree.delStatus && Number.isFinite(ghostImpact)
                ? ghostImpact
                : Number.isFinite(liveImpact)
                  ? liveImpact
                  : 0;
            const heightMeters = Number(tree.userData?.height ?? tree.totalH);
            const trunkRadiusMeters = Number(tree.userData?.trunk_radius);
            return {
                id,
                label: id,
                ghosted: Boolean(tree.delStatus),
                multiplier,
                heightMeters: Number.isFinite(heightMeters) && heightMeters > 0 ? heightMeters : undefined,
                trunkRadiusMeters:
                    Number.isFinite(trunkRadiusMeters) && trunkRadiusMeters > 0
                        ? trunkRadiusMeters
                        : undefined,
            };
        })
        .filter((row): row is PciSiteTree => row != null);
}

export function mergePciSiteTrees(
    prev: PreContractSiteTree[],
    incoming: PciSiteTree[],
): PreContractSiteTree[] {
    if (!incoming.length) return prev;
    const prevById = new Map(prev.map((tree) => [tree.id, tree]));
    return incoming.map((tree) => {
        const old = prevById.get(tree.id);
        return {
            id: tree.id,
            label: tree.label || old?.label || tree.id,
            // Keep the Tree Cutting tab's ghost choice. Shade republishes can
            // briefly report delStatus=false and would otherwise flip the UI.
            ghosted: old ? old.ghosted : tree.ghosted,
            multiplier:
                typeof tree.multiplier === 'number' && Number.isFinite(tree.multiplier)
                    ? tree.multiplier
                    : old?.multiplier ?? 0,
            heightMeters:
                typeof tree.heightMeters === 'number' && Number.isFinite(tree.heightMeters)
                    ? tree.heightMeters
                    : old?.heightMeters,
            trunkRadiusMeters:
                typeof tree.trunkRadiusMeters === 'number' && Number.isFinite(tree.trunkRadiusMeters)
                    ? tree.trunkRadiusMeters
                    : old?.trunkRadiusMeters,
        };
    });
}

export function pciSiteTreesLookEqual(
    a: PreContractSiteTree[],
    b: PreContractSiteTree[],
): boolean {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    return a.every((tree, index) => {
        const other = b[index];
        return (
            other != null &&
            tree.id === other.id &&
            tree.label === other.label &&
            tree.ghosted === other.ghosted &&
            tree.multiplier === other.multiplier &&
            tree.heightMeters === other.heightMeters &&
            tree.trunkRadiusMeters === other.trunkRadiusMeters
        );
    });
}

export function pciGhostedTreeIdKey(trees: Array<{ id: string; ghosted?: boolean }>): string {
    return trees
        .filter((tree) => tree.ghosted)
        .map((tree) => tree.id)
        .sort()
        .join('\0');
}

/** Keep A/B/C tree-id labels visible in the pre-contract viewport embed. */
export function ensurePciTreeIdLabels(viewer: Viewer | null | undefined): void {
    if (!viewer?.treeGroup || !viewer.treeTagGroup) return;

    const existing = new Map<string, any>();
    viewer.treeTagGroup.children.forEach((label: any) => {
        const name = String(label?.name ?? '').trim();
        if (name) existing.set(name, label);
    });

    const seen = new Set<string>();
    viewer.treeGroup.children.forEach((tree: any) => {
        const id = String(tree?.userData?.treeId ?? '').trim();
        if (!id || tree.visible === false) return;
        seen.add(id);
        const color = tree.delStatus ? '#ff3b30' : '#ffffff';
        const height = Number(tree.totalH);
        const y = (Number.isFinite(height) && height > 0 ? height : 4) + 0.45;
        const x = tree.position.x;
        const z = tree.position.z;
        const label = existing.get(id);
        if (!label) {
            viewer.treeTagGroup.add(
                MakeCSSLabel(id, {
                    color,
                    size: 56,
                    x,
                    y,
                    z,
                    visible: true,
                }),
            );
            return;
        }
        label.position.set(x, y, z);
        label.visible = true;
        if (label.labelColor !== color) {
            label.labelColor = color;
            const el = label.element as HTMLElement | undefined;
            if (el) el.style.color = color;
        }
    });

    viewer.treeTagGroup.children.slice().forEach((label: any) => {
        const name = String(label?.name ?? '').trim();
        if (seen.has(name)) return;
        viewer.treeTagGroup.remove(label);
        const el = label.element as HTMLElement | undefined;
        el?.parentNode?.removeChild(el);
    });
    viewer.treeTagGroup.visible = true;
}

/** Ghost/unghost live trees in the embed to match the Tree Cutting tab. */
export function applyPciTreeGhostSelection(
    viewer: Viewer | null | undefined,
    ghostedTreeIds: string[],
    options?: { refreshShade?: boolean },
): boolean {
    const trees = viewer?.treeGroup?.children;
    if (!viewer || !trees?.length) return false;
    const ghosted = new Set(ghostedTreeIds.map((id) => String(id).trim()).filter(Boolean));
    const newlyGhosted: string[] = [];
    const newlyRestored: string[] = [];

    trees.forEach((tree: any) => {
        const id = String(tree?.userData?.treeId ?? tree?.obstId ?? '').trim();
        if (!id) return;
        const next = ghosted.has(id);
        if (Boolean(tree.delStatus) === next) return;
        if (next) {
            ghostTreeForTreeCutScenario(tree);
            newlyGhosted.push(id);
        } else {
            clearTreeCutScenarioGhostState(tree);
            applyTreeGhostAppearance(tree, false);
            newlyRestored.push(id);
        }
    });

    if (!newlyGhosted.length && !newlyRestored.length) {
        return false;
    }

    beginManualGhostToggle();
    const refreshShade = options?.refreshShade !== false;
    try {
        if (refreshShade) {
            if (newlyGhosted.length) viewer.reCalcShade?.(newlyGhosted, true);
            if (newlyRestored.length) viewer.reCalcShade?.(newlyRestored, false);
            viewer.refreshShade?.();
        }
        ensurePciTreeIdLabels(viewer);
        viewer.render?.();
    } catch {
        /* ignore */
    }
    return true;
}

/** Switch losses vendor and refresh shade — same outcome as LossesVendorSection Confirm. */
export function applyPciLossesVendor(
    viewer: Viewer | null | undefined,
    vendorRaw: string,
    options?: { force?: boolean; skipShade?: boolean },
): boolean {
    if (!viewer) return false;
    const vendor = normalizeLossesVendor(vendorRaw);
    const current = normalizeLossesVendor(useViewerStore.getState().selectedLossesVendor);
    if (current === vendor && !options?.force) return false;

    useViewerStore.setState({
        selectedLossesVendor: vendor,
        refreshingShade: options?.skipShade ? useViewerStore.getState().refreshingShade : true,
    });
    if (options?.skipShade) return true;
    try {
        const faceCount = Number(viewer.roofs?.[0]?.faces?.length) || 1;
        siteCalculationState.needsUpdate(ACCESS_MULTIPLIERS, Math.max(0, faceCount - 1));
        siteCalculationState.useCalculations(ACCESS_MULTIPLIERS);
        RefreshShade();
    } catch {
        /* ignore — vendor is still stored for Open in OneDraw */
    }
    return true;
}

export function postPciTreeGhostToEmbed(ghostedTreeIds: string[]): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(
            {
                type: PCI_ONEDRAW_TREE_GHOST_MSG,
                ghostedTreeIds,
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

/** Stop the embed shade worker so the next design selection can start immediately. */
export function postPciCancelShadeToEmbed(): void {
    abortPendingPciViewportRequest();
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) {
        cancelShadeCalculation();
        return;
    }
    try {
        win.postMessage({ type: PCI_ONEDRAW_CANCEL_SHADE_MSG }, window.location.origin);
    } catch {
        cancelShadeCalculation();
    }
}

/** Apply a losses vendor in the warm embed and refresh shade (same path as Losses toggle). */
export function postPciLossesVendorToEmbed(
    vendor: string,
    options?: { force?: boolean; skipShade?: boolean; generate?: boolean },
): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(
            {
                type: PCI_ONEDRAW_LOSSES_VENDOR_MSG,
                vendor,
                force: options?.force === true,
                skipShade: options?.skipShade === true,
                generate: options?.generate === true,
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

/** Ask the warm embed to remeasure its canvas after docking into the Design drawer. */
export function postPciViewportResizeToEmbed(): void {
    if (typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage({ type: PCI_ONEDRAW_VIEWPORT_RESIZE_MSG }, window.location.origin);
        win.dispatchEvent(new Event('resize'));
    } catch {
        /* ignore */
    }
}

export function collectIncludedModuleIds(
    layouts: Array<{ modules: Array<{ id: string; included: boolean; meshId?: string } | null> }>,
): string[] {
    const ids: string[] = [];
    layouts.forEach((layout) => {
        layout.modules.forEach((cell) => {
            if (!cell?.included) return;
            if (cell.id) ids.push(cell.id);
            if (cell.meshId) ids.push(cell.meshId);
        });
    });
    return ids;
}

/** Every placed module on the board, selected or not (max-fill catalog). */
export function collectAllModuleIds(
    layouts: Array<{ modules: Array<{ id: string; included: boolean; meshId?: string } | null> }>,
): string[] {
    const ids: string[] = [];
    layouts.forEach((layout) => {
        layout.modules.forEach((cell) => {
            if (!cell) return;
            if (cell.id) ids.push(cell.id);
            if (cell.meshId) ids.push(cell.meshId);
        });
    });
    return ids;
}

/**
 * Keep only the included modules in the live OneDraw viewer (Open in OneDraw from AI options).
 * Prefers an exact placement snapshot from the pre-contract embed so the 3D layout matches the card.
 * Always filters to `includedModuleIds` after restore so a full max-fill snapshot cannot leak through.
 * @returns true when a layout was applied (caller can drop the pending payload).
 */
export async function applyPciIncludedModulesToViewer(
    viewer: Viewer | null | undefined,
    includedModuleIds: string[],
    modulesLayout?: SavedRoofModulesLayout | null,
): Promise<boolean> {
    if (!viewer) return false;

    let restoredFromSnapshot = false;
    if (Array.isArray(modulesLayout) && modulesLayout.length > 0 && viewer.roofMeshArr?.length) {
        const remapped = remapModulesLayoutToViewerWithMap(viewer, modulesLayout).layout;
        const moduleCount = remapped.reduce((sum, roof) => sum + (roof.moduleArr?.length ?? 0), 0);
        if (moduleCount > 0) {
            const stamps = collectPciCellIdStamps(viewer);
            await restoreModulesLayoutFromSnapshot(viewer, remapped);
            applyPciCellIdStamps(viewer, stamps);
            await waitForPciShadeAndGraph(viewer);
            restoredFromSnapshot = true;
        }
    }

    if (!viewer.moduleMeshArr?.length) return restoredFromSnapshot;
    const included = new Set(
        includedModuleIds.map((id) => String(id).trim().toUpperCase()).filter(Boolean),
    );
    if (included.size === 0) return restoredFromSnapshot;

    applyPciModuleSelectionHighlight(viewer, includedModuleIds);

    const keepCount = viewer.moduleMeshArr.filter((mesh: any) => meshMatchesIncluded(mesh, included)).length;
    if (keepCount === 0) return restoredFromSnapshot;

    const toDeleteByRoof = new Map<any, any[]>();
    [...viewer.moduleMeshArr].forEach((mesh: any) => {
        if (meshMatchesIncluded(mesh, included)) return;
        const roof = mesh?.parent;
        if (!roof) return;
        const list = toDeleteByRoof.get(roof) ?? [];
        list.push(mesh);
        toDeleteByRoof.set(roof, list);
    });

    toDeleteByRoof.forEach((selModules, selRoof) => {
        if (!selModules.length) return;
        try {
            DeleteModule(selModules, selRoof, viewer.moduleMeshArr, viewer, true);
        } catch {
            /* keep remaining modules if a roof delete fails */
        }
    });

    try {
        useViewerStore.setState({ savedRoofModulesLayout: captureLiveModulesLayout(viewer) });
    } catch {
        /* ignore store snapshot */
    }

    try {
        await SetModuleGraph(viewer);
    } catch {
        /* production graph can catch up on the next calc */
    }

    try {
        viewer.render?.();
    } catch {
        /* ignore */
    }
    return true;
}

/** Payload to place a selected AI option into the parked pre-contract embed for 3D preview. */
export type PciApplyDesignOptionPayload = {
    includedModuleIds: string[];
    modulesLayout?: SavedRoofModulesLayout | null;
    ghostedTreeIds?: string[];
    /** Optional full max-fill restore before subsetting, when modulesLayout is missing. */
    fillLayout?: SavedRoofModulesLayout | null;
    /**
     * Re-place modules without running the shade worker. Used by panel toggles, where the
     * board already holds per-module shade from the design that is on screen.
     */
    skipShadeRefresh?: boolean;
};

/**
 * Place one AI option into the live embed and leave it there for the 3D popup
 * (unlike simulate, which restores the baseline afterward).
 */
export async function applyPciDesignOptionToViewer(
    viewer: Viewer | null | undefined,
    payload: PciApplyDesignOptionPayload,
): Promise<boolean> {
    if (!viewer?.roofMeshArr?.length) return false;
    const includedModuleIds = Array.isArray(payload.includedModuleIds)
        ? payload.includedModuleIds
        : [];
    const ghostedTreeIds = Array.isArray(payload.ghostedTreeIds) ? payload.ghostedTreeIds : [];
    const skipShade = payload.skipShadeRefresh === true;

    if (ghostedTreeIds.length > 0 || Array.isArray(payload.ghostedTreeIds)) {
        applyPciTreeGhostSelection(viewer, ghostedTreeIds, { refreshShade: !skipShade });
        if (!skipShade) await waitForPciShadeAndGraph(viewer);
    }

    if (payload.modulesLayout?.length) {
        const applied = await applyPciIncludedModulesToViewer(
            viewer,
            includedModuleIds,
            payload.modulesLayout,
        );
        if (applied) {
            preparePci3dView(viewer);
            return true;
        }
    }

    if (payload.fillLayout?.length) {
        const fillCount = payload.fillLayout.reduce(
            (sum, roof) => sum + (roof.moduleArr?.length ?? 0),
            0,
        );
        if ((viewer.moduleMeshArr?.length ?? 0) < fillCount) {
            try {
                const remapped = remapModulesLayoutToViewerWithMap(viewer, payload.fillLayout).layout;
                await restoreModulesLayoutFromSnapshot(viewer, remapped, {
                    skipShadeRefresh: skipShade,
                });
                if (!skipShade) await waitForPciShadeAndGraph(viewer);
            } catch {
                /* fall through to filtering whatever is live */
            }
        }
        // Restored meshes come back without their R#M# stamps; numbering the complete fill
        // here reproduces the ids the board was built from.
        ensureViewerPciCellIdsStamped(viewer);
        // Hide instead of delete. A panel the user re-selects has to still be in the scene,
        // and capturing the subset to re-place it wipes every roof whose ids fail to match.
        applyPciModuleSelectionHighlight(viewer, includedModuleIds);
        preparePci3dView(viewer);
        return true;
    }

    const applied = await applyPciIncludedModulesToViewer(viewer, includedModuleIds, null);
    if (applied) preparePci3dView(viewer);
    return applied;
}

/** Ask the parked embed to restore a selected AI option for 3D preview. */
export function postPciApplyDesignOptionToEmbed(
    payload: PciApplyDesignOptionPayload,
    requestId?: string,
): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(
            {
                type: PCI_ONEDRAW_APPLY_DESIGN_MSG,
                ...(requestId ? { requestId } : {}),
                includedModuleIds: payload.includedModuleIds ?? [],
                modulesLayout: payload.modulesLayout ?? null,
                ghostedTreeIds: payload.ghostedTreeIds ?? [],
                fillLayout: payload.fillLayout ?? null,
                skipShadeRefresh: payload.skipShadeRefresh === true,
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

/**
 * Apply a design to the persistent background viewer and resolve when that
 * same viewer confirms the restore completed.
 */
export function requestPciApplyDesignOptionToEmbed(
    payload: PciApplyDesignOptionPayload,
    timeoutMs = 120000,
): Promise<boolean> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(false);
    }
    const frame = document.getElementById(
        PCI_ONEDRAW_EMBED_FRAME_ID,
    ) as HTMLIFrameElement | null;
    if (!frame?.contentWindow) return Promise.resolve(false);
    const requestId = `pci-apply-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            release();
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(ok);
        };
        const release = trackPciViewportRequest(() => finish(false));
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                requestId?: string;
                ok?: boolean;
            } | null;
            if (data?.type !== PCI_ONEDRAW_APPLY_DESIGN_DONE_MSG) return;
            if (data.requestId !== requestId) return;
            finish(data.ok === true);
        };
        const timer = window.setTimeout(() => finish(false), timeoutMs);
        window.addEventListener('message', onMessage);
        postPciApplyDesignOptionToEmbed(payload, requestId);
    });
}

export type PciLoadDesignVersionPayload = {
    salesForceId: string;
    designNo: number;
    versionNo: number;
};

/** Ask the parked embed to load a finalized oneDRAW design/version into the 3D viewport. */
export function requestPciLoadDesignVersionToEmbed(
    payload: PciLoadDesignVersionPayload,
    timeoutMs = 180_000,
): Promise<boolean> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(false);
    }
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return Promise.resolve(false);
    const requestId = `pci-version-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            release();
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(ok);
        };
        const release = trackPciViewportRequest(() => finish(false));
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                requestId?: string;
                ok?: boolean;
            } | null;
            if (data?.type !== PCI_ONEDRAW_LOAD_VERSION_DONE_MSG) return;
            if (data.requestId !== requestId) return;
            finish(data.ok === true);
        };
        const timer = window.setTimeout(() => finish(false), timeoutMs);
        window.addEventListener('message', onMessage);
        try {
            win.postMessage(
                {
                    type: PCI_ONEDRAW_LOAD_VERSION_MSG,
                    requestId,
                    salesForceId: payload.salesForceId,
                    designNo: payload.designNo,
                    versionNo: payload.versionNo,
                },
                window.location.origin,
            );
        } catch {
            finish(false);
        }
    });
}

/** Exact 3D placement for the included modules currently in the viewer. */
export function captureIncludedModulesLayout(
    viewer: Viewer | null | undefined,
    includedModuleIds: string[],
): SavedRoofModulesLayout {
    if (!viewer?.roofMeshArr?.length) return [];
    const included = new Set(
        includedModuleIds.map((id) => String(id).trim().toUpperCase()).filter(Boolean),
    );
    if (included.size === 0) return [];

    const layout: SavedRoofModulesLayout = [];
    viewer.roofMeshArr.forEach((roof: any) => {
        const meshes = getRoofModuleMeshes(roof).filter((mesh) => meshMatchesIncluded(mesh, included));
        if (!meshes.length) return;
        const userData = roof.userData ?? {};
        const roofTagId =
            typeof userData.roof_tag_id === 'number' && Number.isFinite(userData.roof_tag_id)
                ? userData.roof_tag_id
                : undefined;
        const roofNumber =
            typeof userData.roof_number_from_aurora === 'number' &&
            Number.isFinite(userData.roof_number_from_aurora)
                ? userData.roof_number_from_aurora
                : roofTagId;
        const nameOverride =
            typeof userData.nameOverride === 'number' && Number.isFinite(userData.nameOverride)
                ? userData.nameOverride
                : undefined;
        layout.push({
            roofFaceId: String(roof.roofFaceId ?? ''),
            moduleArr: meshes.map((mesh) => getModuleMeshInfo(mesh)),
            ...(roofTagId != null ? { roofTagId } : {}),
            ...(roofNumber != null ? { roofNumber } : {}),
            ...(nameOverride != null ? { nameOverride } : {}),
        });
    });
    return layout.filter((entry) => entry.roofFaceId && entry.moduleArr.length > 0);
}

/** Ask the parked pre-contract embed for an exact modulesLayout of the selected option. */
export function requestPciModulesLayoutFromEmbed(
    includedModuleIds: string[],
): Promise<SavedRoofModulesLayout | null> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(null);
    }
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win || includedModuleIds.length === 0) return Promise.resolve(null);

    return new Promise((resolve) => {
        let settled = false;
        const finish = (layout: SavedRoofModulesLayout | null) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(layout);
        };

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as { type?: string; layout?: SavedRoofModulesLayout } | null;
            if (data?.type !== PCI_ONEDRAW_MODULES_LAYOUT_MSG) return;
            const layout = Array.isArray(data.layout) ? data.layout : [];
            finish(layout.length > 0 ? layout : null);
        };

        const timer = window.setTimeout(() => finish(null), 2500);
        window.addEventListener('message', onMessage);
        try {
            win.postMessage(
                {
                    type: PCI_ONEDRAW_CAPTURE_MODULES_MSG,
                    includedModuleIds,
                },
                window.location.origin,
            );
        } catch {
            finish(null);
        }
    });
}

/** One AI option to place and measure in the live embed viewer. */
export type PciSimulateOptionJob = {
    id: string;
    includedModuleIds: string[];
    /** Restore this orientation's max-fill before measuring the subset. */
    fillOrientation?: PreContractFillOrientation;
};

/** Viewer production after placing an option — same source as the Production panel. */
export type PciSimulatedOptionResult = {
    id: string;
    annualKwh: number;
    utilityKwh: number;
    averageSolarAccess: number;
    moduleCount: number;
    systemKw: number;
    efficiency: number | null;
    /** Weighted module multiplier from the live Production calc when available. */
    multiplier: number | null;
    modulesLayout: SavedRoofModulesLayout;
    solarAccessByModuleId: Record<string, number>;
    /** Live mesh production (kWh) keyed by module id — same source as Production panel. */
    productionByModuleId: Record<string, number>;
    multiplierByModuleId: Record<string, number>;
};

function collectSolarAccessByModuleId(viewer: Viewer | null | undefined): Record<string, number> {
    const out: Record<string, number> = {};
    viewer?.moduleMeshArr?.forEach((mesh: any) => {
        const sa = solarAccessFromMesh(mesh);
        pciModuleKeys(mesh).forEach((key) => {
            out[key] = sa;
        });
    });
    return out;
}

function collectProductionByModuleId(viewer: Viewer | null | undefined): Record<string, number> {
    const out: Record<string, number> = {};
    viewer?.moduleMeshArr?.forEach((mesh: any) => {
        const production = Number(mesh?.production);
        if (!Number.isFinite(production) || production <= 0) return;
        const rounded = Math.round(production);
        pciModuleKeys(mesh).forEach((key) => {
            out[key.trim().toUpperCase()] = rounded;
        });
    });
    return out;
}

function collectMultiplierByModuleId(viewer: Viewer | null | undefined): Record<string, number> {
    const out: Record<string, number> = {};
    viewer?.moduleMeshArr?.forEach((mesh: any) => {
        const multiplier = Number(mesh?.multiplier);
        if (!Number.isFinite(multiplier) || multiplier <= 0) return;
        const rounded = Math.round(multiplier * 1000) / 1000;
        pciModuleKeys(mesh).forEach((key) => {
            out[key.trim().toUpperCase()] = rounded;
        });
    });
    return out;
}

function readPciSimulationMetrics(
    viewer: Viewer,
    id: string,
    fallbackLayout: SavedRoofModulesLayout,
): PciSimulatedOptionResult {
    const state = useViewerStore.getState() as {
        actualWattage?: number;
        utilityWattage?: number;
        weightedSolarAverage?: number | string;
        modeCount?: number;
        totalSystemSize?: number;
        efficiency?: number;
        multiplier?: number;
        roofInfoArr?: Array<{
            efficiency?: number | string;
            utility_production?: number | string;
            modules?: number | string;
        }>;
    };
    let annualKwh = Math.round(Number(state.actualWattage) || 0);
    let utilityKwh = Math.round(Number(state.utilityWattage) || 0);
    const averageSolarAccess = Math.round(Number(state.weightedSolarAverage) || 0);
    const moduleCount =
        typeof state.modeCount === 'number' && Number.isFinite(state.modeCount)
            ? state.modeCount
            : fallbackLayout.reduce((sum, roof) => sum + (roof.moduleArr?.length ?? 0), 0);
    const systemKw =
        typeof state.totalSystemSize === 'number' && Number.isFinite(state.totalSystemSize)
            ? Math.round(state.totalSystemSize * 1000) / 1000
            : 0;
    let efficiency =
        typeof state.efficiency === 'number' && Number.isFinite(state.efficiency)
            ? state.efficiency
            : null;
    const rawMultiplier =
        typeof state.multiplier === 'number' && Number.isFinite(state.multiplier)
            ? state.multiplier
            : null;
    const multiplier =
        rawMultiplier != null && rawMultiplier > 0
            ? Math.round(rawMultiplier * 1000) / 1000
            : null;

    // Roof-level Production rows are the same source as the panel; use them when
    // store totals were still 0 after a fast layout restore.
    const roofs = Array.isArray(state.roofInfoArr) ? state.roofInfoArr : [];
    if (roofs.length > 0 && (utilityKwh <= 0 || efficiency == null || efficiency <= 0)) {
        let utilSum = 0;
        let effWeighted = 0;
        let effModules = 0;
        roofs.forEach((roof) => {
            const util = Number(roof.utility_production);
            if (Number.isFinite(util) && util > 0) utilSum += util;
            const eff = Number(roof.efficiency);
            const mods = Number(roof.modules);
            if (Number.isFinite(eff) && eff > 0 && Number.isFinite(mods) && mods > 0) {
                effWeighted += eff * mods;
                effModules += mods;
            }
        });
        if (utilityKwh <= 0 && utilSum > 0) utilityKwh = Math.round(utilSum);
        if ((efficiency == null || efficiency <= 0) && effModules > 0) {
            efficiency = Math.round(effWeighted / effModules);
        }
    }

    if (annualKwh <= 0) {
        const meshProd = (viewer.moduleMeshArr ?? []).reduce((sum: number, mesh: any) => {
            const production = Number(mesh?.production);
            return sum + (Number.isFinite(production) && production > 0 ? production : 0);
        }, 0);
        if (meshProd > 0) annualKwh = Math.round(meshProd);
    }

    const liveLayout = captureLiveModulesLayout(viewer);
    return {
        id,
        annualKwh,
        utilityKwh,
        averageSolarAccess,
        moduleCount,
        systemKw,
        efficiency,
        multiplier,
        modulesLayout: liveLayout.length > 0 ? liveLayout : fallbackLayout,
        solarAccessByModuleId: collectSolarAccessByModuleId(viewer),
        productionByModuleId: collectProductionByModuleId(viewer),
        multiplierByModuleId: collectMultiplierByModuleId(viewer),
    };
}

/**
 * Place each AI option in the embed (same restore + SetModuleGraph path as Run AI)
 * and read kWh Actual / solar access from the live Production calc.
 */
export async function simulatePciOptionsInViewer(
    viewer: Viewer | null | undefined,
    jobs: PciSimulateOptionJob[],
    fills?: Partial<Record<PreContractFillOrientation, SavedRoofModulesLayout>>,
    ghostedTreeIds?: string[],
): Promise<PciSimulatedOptionResult[]> {
    if (!viewer?.roofMeshArr?.length || jobs.length === 0) return [];

    const baseline = captureLiveModulesLayout(viewer);
    const previousGhosted = collectPciSiteTrees(viewer)
        .filter((tree) => tree.ghosted)
        .map((tree) => tree.id);
    const applyGhosts = Array.isArray(ghostedTreeIds);

    if (applyGhosts) {
        applyPciTreeGhostSelection(viewer, ghostedTreeIds, { refreshShade: true });
        await waitForPciShadeAndGraph(viewer);
    }

    const results: PciSimulatedOptionResult[] = [];
    try {
        for (const job of jobs) {
            try {
                const fill =
                    job.fillOrientation && fills?.[job.fillOrientation]?.length
                        ? fills[job.fillOrientation]
                        : null;
                if (fill?.length) {
                    await restoreModulesLayoutFromSnapshot(viewer, fill);
                }
                const layout = captureIncludedModulesLayout(viewer, job.includedModuleIds);
                if (!layout.length) continue;
                await restoreModulesLayoutFromSnapshot(viewer, layout);
                try {
                    await SetModuleGraph(viewer);
                } catch {
                    /* metrics may still be readable from the prior graph pass */
                }
                results.push(readPciSimulationMetrics(viewer, job.id, layout));
            } catch {
                /* skip this option; parent keeps the heuristic estimate */
            }
        }
    } finally {
        if (applyGhosts) {
            applyPciTreeGhostSelection(viewer, previousGhosted, { refreshShade: false });
        }
        if (baseline.length) {
            try {
                await restoreModulesLayoutFromSnapshot(viewer, baseline);
            } catch {
                /* ignore restore */
            }
        }
    }

    return results;
}

/** Ask the parked embed to simulate each option and return live production. */
export function requestPciSimulateOptionsFromEmbed(
    jobs: PciSimulateOptionJob[],
    fills?: Partial<Record<PreContractFillOrientation, SavedRoofModulesLayout>>,
    ghostedTreeIds?: string[],
): Promise<PciSimulatedOptionResult[] | null> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(null);
    }
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win || jobs.length === 0) return Promise.resolve(null);

    return new Promise((resolve) => {
        let settled = false;
        const finish = (results: PciSimulatedOptionResult[] | null) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(results);
        };

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as { type?: string; results?: PciSimulatedOptionResult[] } | null;
            if (data?.type !== PCI_ONEDRAW_SIMULATE_RESULTS_MSG) return;
            finish(Array.isArray(data.results) ? data.results : []);
        };

        const timer = window.setTimeout(() => finish(null), 120000);
        window.addEventListener('message', onMessage);
        try {
            win.postMessage(
                {
                    type: PCI_ONEDRAW_SIMULATE_OPTIONS_MSG,
                    jobs,
                    fills: fills ?? {},
                    ghostedTreeIds: ghostedTreeIds ?? null,
                },
                window.location.origin,
            );
        } catch {
            finish(null);
        }
    });
}

export type PciMaxfillModule = {
    id: string;
    roofLabel: string;
    roofTagId: number;
    solarAccess: number;
    multiplier: number;
    productionKwh: number;
};

export type PciMaxfillSnapshot = {
    ready: boolean;
    layouts: PreContractRoofModuleLayout[];
    modules: PciMaxfillModule[];
    annualKwh: number;
    averageSolarAccess: number;
    moduleCount: number;
    systemKw: number;
    efficiency: number | null;
    orientation?: PreContractFillOrientation;
    modulesLayout?: SavedRoofModulesLayout;
};

const FILL_DIR: Record<PreContractFillOrientation, 'port' | 'land' | 'both'> = {
    portrait: 'port',
    landscape: 'land',
    both: 'both',
};

/**
 * Place a live max-fill on every eligible roof and leave it there (unlike capture,
 * which restores the previous layout). Used by AI Config so trimmed applied designs
 * do not leave empty roofs in the viewport.
 */
export async function applyPciMaxFillToViewer(
    viewer: Viewer | null | undefined,
    orientation: PreContractFillOrientation = 'portrait',
): Promise<boolean> {
    if (!viewer?.roofMeshArr?.length) return false;
    const dir = FILL_DIR[orientation] ?? 'port';
    try {
        addModulesToAllRoofsMaxFill(viewer, null, dir);
    } catch {
        return false;
    }
    await waitForPciShadeAndGraph(viewer);
    ensureViewerPciCellIdsStamped(viewer);
    applyPciShowAllModules(viewer);
    try {
        postPciSiteRoofsToParent(viewer);
    } catch {
        /* parent still has the catalog */
    }
    try {
        viewer.render?.();
    } catch {
        /* ignore */
    }
    return (viewer.moduleMeshArr?.length ?? 0) > 0;
}

export function postPciApplyMaxFillToEmbed(
    orientation: PreContractFillOrientation,
    requestId?: string,
): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return;
    try {
        win.postMessage(
            {
                type: PCI_ONEDRAW_APPLY_MAXFILL_MSG,
                orientation,
                ...(requestId ? { requestId } : {}),
            },
            window.location.origin,
        );
    } catch {
        /* ignore */
    }
}

export function requestPciApplyMaxFillToEmbed(
    orientation: PreContractFillOrientation = 'portrait',
    timeoutMs = 120000,
): Promise<boolean> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve(false);
    }
    const frame = document.getElementById(
        PCI_ONEDRAW_EMBED_FRAME_ID,
    ) as HTMLIFrameElement | null;
    if (!frame?.contentWindow) return Promise.resolve(false);
    const requestId = `pci-maxfill-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    return new Promise((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
            if (settled) return;
            settled = true;
            release();
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(ok);
        };
        const release = trackPciViewportRequest(() => finish(false));
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                requestId?: string;
                ok?: boolean;
            } | null;
            if (data?.type !== PCI_ONEDRAW_APPLY_MAXFILL_DONE_MSG) return;
            if (data.requestId !== requestId) return;
            finish(data.ok === true);
        };
        const timer = window.setTimeout(() => finish(false), timeoutMs);
        window.addEventListener('message', onMessage);
        postPciApplyMaxFillToEmbed(orientation, requestId);
    });
}

function collectPciMaxfillModules(viewer: Viewer | null | undefined): PciMaxfillModule[] {
    const roofs = collectPciSiteRoofs(viewer);
    const byTag = new Map(roofs.map((roof) => [roof.roofTagId, roof]));
    const modules: PciMaxfillModule[] = [];
    viewer?.moduleMeshArr?.forEach((mesh: any) => {
        const keys = pciModuleKeys(mesh);
        const id = keys[0];
        if (!id) return;
        const tag = Number(mesh?.parent?.userData?.roof_tag_id);
        const roof = Number.isFinite(tag) ? byTag.get(tag) : undefined;
        const production = Number(mesh.production);
        const multiplier = Number(mesh.multiplier);
        modules.push({
            id,
            roofLabel: roof?.label ?? (Number.isFinite(tag) ? `Roof ${tag}` : 'Roof'),
            roofTagId: Number.isFinite(tag) ? tag : 0,
            solarAccess: solarAccessFromMesh(mesh),
            multiplier: Number.isFinite(multiplier) ? multiplier : 0,
            productionKwh: Number.isFinite(production) && production > 0 ? Math.round(production) : 0,
        });
    });
    return modules;
}

/** Max-fill with real shade: wait for irrPro/multipliers, then serialize every module. */
export async function capturePciMaxfillSnapshot(
    viewer: Viewer | null | undefined,
): Promise<PciMaxfillSnapshot | null> {
    if (!viewer?.roofMeshArr?.length) return null;
    const ready = await waitForPciShadeAndGraph(viewer);
    const state = useViewerStore.getState() as {
        actualWattage?: number;
        weightedSolarAverage?: number | string;
        modeCount?: number;
        totalSystemSize?: number;
        efficiency?: number;
    };
    const layouts = collectPciRoofModuleLayouts(viewer);
    const modules = collectPciMaxfillModules(viewer);
    const saSum = modules.reduce((sum, row) => sum + row.solarAccess, 0);
    const modulesLayout = captureLiveModulesLayout(viewer);
    return {
        ready,
        layouts,
        modules,
        annualKwh: Math.round(Number(state.actualWattage) || 0),
        averageSolarAccess:
            modules.length > 0
                ? Math.round(saSum / modules.length)
                : Math.round(Number(state.weightedSolarAverage) || 0),
        moduleCount:
            typeof state.modeCount === 'number' && state.modeCount > 0
                ? state.modeCount
                : modules.length,
        systemKw:
            typeof state.totalSystemSize === 'number' && Number.isFinite(state.totalSystemSize)
                ? Math.round(state.totalSystemSize * 1000) / 1000
                : 0,
        efficiency:
            typeof state.efficiency === 'number' && Number.isFinite(state.efficiency)
                ? state.efficiency
                : null,
        ...(modulesLayout.length ? { modulesLayout } : {}),
    };
}

/** Fill each selected orientation, wait for shade, and serialize the max-fill. */
export async function capturePciMaxfillStrategies(
    viewer: Viewer | null | undefined,
    orientations: PreContractFillOrientation[],
): Promise<PciMaxfillSnapshot[]> {
    if (!viewer?.roofMeshArr?.length) return [];
    const selected = normalizeFillOrientations(orientations);
    const baseline = captureLiveModulesLayout(viewer);
    const snapshots: PciMaxfillSnapshot[] = [];

    for (const orientation of selected) {
        try {
            addModulesToAllRoofsMaxFill(viewer, null, FILL_DIR[orientation]);
        } catch {
            continue;
        }
        const snap = await capturePciMaxfillSnapshot(viewer);
        if (!snap || snap.modules.length === 0) continue;
        snapshots.push({ ...snap, orientation });
    }

    if (baseline.length) {
        try {
            await restoreModulesLayoutFromSnapshot(viewer, baseline);
        } catch {
            /* parent boards still hold the last published layout */
        }
    }

    return snapshots;
}

/** Ask the parked embed for a max-fill snapshot after real shade. */
export function requestPciMaxfillFromEmbed(): Promise<PciMaxfillSnapshot | null> {
    return requestPciMaxfillStrategiesFromEmbed(['portrait']).then(
        (snapshots) => snapshots[0] ?? null,
    );
}

/** Ask the parked embed to max-fill each selected orientation and return snapshots. */
export function requestPciMaxfillStrategiesFromEmbed(
    orientations: PreContractFillOrientation[],
): Promise<PciMaxfillSnapshot[]> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.resolve([]);
    }
    const frame = document.getElementById(PCI_ONEDRAW_EMBED_FRAME_ID) as HTMLIFrameElement | null;
    const win = frame?.contentWindow;
    if (!win) return Promise.resolve([]);
    const selected = normalizeFillOrientations(orientations);

    return new Promise((resolve) => {
        let settled = false;
        const finish = (snapshots: PciMaxfillSnapshot[]) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            window.removeEventListener('message', onMessage);
            resolve(snapshots);
        };

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            const data = event.data as {
                type?: string;
                snapshot?: PciMaxfillSnapshot;
                snapshots?: PciMaxfillSnapshot[];
            } | null;
            if (data?.type !== PCI_ONEDRAW_MAXFILL_RESULT_MSG) return;
            if (Array.isArray(data.snapshots) && data.snapshots.length > 0) {
                finish(data.snapshots.filter((row) => row && Array.isArray(row.layouts)));
                return;
            }
            if (data.snapshot && Array.isArray(data.snapshot.layouts)) {
                finish([data.snapshot]);
                return;
            }
            finish([]);
        };

        const timer = window.setTimeout(() => finish([]), 240000);
        window.addEventListener('message', onMessage);
        try {
            win.postMessage(
                { type: PCI_ONEDRAW_MAXFILL_MSG, orientations: selected },
                window.location.origin,
            );
        } catch {
            finish([]);
        }
    });
}
